import type { ApiResponseSnapshot, Env, RequestSpec } from '@/types';
import { getEndpoints } from './endpoints';

export function buildUrl(env: Env, spec: RequestSpec, apiBaseOverride?: string): string {
  const base = apiBaseOverride || getEndpoints(env).apiBase;
  const url = new URL(spec.path.startsWith('/') ? base + spec.path : base + '/' + spec.path);
  if (spec.query) {
    for (const [k, v] of Object.entries(spec.query)) {
      if (v === null || v === undefined || v === '') continue;
      url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

export async function executeRequest(
  env: Env,
  spec: RequestSpec,
  bearerToken: string | null,
  apiBaseOverride?: string
): Promise<ApiResponseSnapshot> {
  const url = buildUrl(env, spec, apiBaseOverride);
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(spec.headers || {}),
  };
  if (bearerToken) headers.Authorization = `Bearer ${bearerToken}`;

  const init: RequestInit = { method: spec.method, headers };
  if (spec.body !== undefined && spec.method !== 'GET' && spec.method !== 'DELETE') {
    headers['Content-Type'] = headers['Content-Type'] || 'application/json';
    init.body = typeof spec.body === 'string' ? spec.body : JSON.stringify(spec.body);
  }

  const start = performance.now();
  let response: Response;
  try {
    response = await fetch(url, init);
  } catch (err) {
    return {
      status: 0,
      ok: false,
      durationMs: performance.now() - start,
      body: { error: 'NetworkError', message: err instanceof Error ? err.message : String(err) },
      headers: {},
    };
  }
  const durationMs = performance.now() - start;

  const respHeaders: Record<string, string> = {};
  response.headers.forEach((value, key) => {
    respHeaders[key] = value;
  });

  const text = await response.text();
  let parsed: unknown = text;
  if (text && (respHeaders['content-type'] || '').includes('application/json')) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  return {
    status: response.status,
    ok: response.ok,
    durationMs,
    body: parsed,
    headers: respHeaders,
  };
}
